import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiPost } from '../client'
import { ApiError } from '../errors'
import { dealsKey } from './deals'
import { jobsKey } from './jobs'
import type { components } from '../generated/schema'

type AnalysisAccepted = components['schemas']['AnalysisAcceptedOut']

export interface DuplicateCandidate {
  deal_id: string
  company_name: string
}

export interface DuplicateDeal {
  message: string
  extractedCompanyName: string | null
  candidates: DuplicateCandidate[]
}

/**
 * The backend refuses a fresh upload that looks like a follow-up, rather than
 * silently creating a second deal. That refusal is a question for the user, not
 * an error: attach to the existing deal, or confirm this really is a new one.
 */
export function asDuplicateDeal(error: unknown): DuplicateDeal | null {
  if (!(error instanceof ApiError) || error.kind !== 'conflict') return null
  // ApiError.detail carries the whole response body; the backend nests the
  // useful part under `detail`.
  const detail = (error.detail as { detail?: unknown } | null)?.detail
  if (!detail || typeof detail !== 'object') return null
  const record = detail as Record<string, unknown>
  if (record.code !== 'possible_duplicate_deal') return null
  return {
    message: typeof record.message === 'string' ? record.message : 'This may be a duplicate deal.',
    extractedCompanyName:
      typeof record.extracted_company_name === 'string' ? record.extracted_company_name : null,
    candidates: Array.isArray(record.candidates) ? (record.candidates as DuplicateCandidate[]) : [],
  }
}

export interface UploadRefusal {
  code: 'duplicate_transcript' | 'deal_not_active'
  message: string
  /** The deal the refusal is about, when there is one to point at. */
  companyName: string | null
  dealId: string | null
  /** duplicate_transcript only: which call it was, and when it was analysed. */
  callNumber: number | null
  analysedAt: string | null
}

/**
 * The other two refusals, both added 2026-09-01 and both questions rather than
 * errors: this call has already been analysed, and this deal is closed. Each used
 * to be accepted in silence — charging a credit, and in the duplicate's case telling
 * the deal engine the deal had moved when nothing had.
 */
export function asUploadRefusal(error: unknown): UploadRefusal | null {
  if (!(error instanceof ApiError) || error.kind !== 'conflict') return null
  const detail = (error.detail as { detail?: unknown } | null)?.detail
  if (!detail || typeof detail !== 'object') return null
  const record = detail as Record<string, unknown>
  if (record.code !== 'duplicate_transcript' && record.code !== 'deal_not_active') return null
  const str = (key: string) => (typeof record[key] === 'string' ? (record[key] as string) : null)
  return {
    code: record.code as UploadRefusal['code'],
    message: str('message') ?? 'This upload needs your confirmation.',
    companyName: str('company_name'),
    dealId: str('deal_id'),
    callNumber: typeof record.call_number === 'number' ? record.call_number : null,
    analysedAt: str('analysed_at'),
  }
}

export interface UploadInput {
  file: File
  /** Attach to this deal, or omit to create a new one. */
  dealId?: string
  companyName?: string
  country?: string
  /** Only after the user has seen the duplicate question and chosen "new deal". */
  forceNewDeal?: boolean
  /** Only after the user has been told this call was already analysed. */
  forceDuplicateTranscript?: boolean
  /** Only after the user has been told the deal is closed. */
  forceInactiveDeal?: boolean
}

export function useUploadTranscript() {
  const queryClient = useQueryClient()

  return useMutation<AnalysisAccepted, Error, UploadInput>({
    mutationFn: (input) => {
      const form = new FormData()
      form.append('file', input.file)
      form.append('mode', input.dealId ? 'existing_deal' : 'new_deal')
      if (input.dealId) form.append('deal_id', input.dealId)
      if (input.companyName) form.append('company_name', input.companyName)
      if (input.country) form.append('country', input.country)
      if (input.forceNewDeal) form.append('force_new_deal', 'true')
      if (input.forceDuplicateTranscript) form.append('force_duplicate_transcript', 'true')
      if (input.forceInactiveDeal) form.append('force_inactive_deal', 'true')
      return apiPost<AnalysisAccepted>('/analysis/transcripts', form)
    },
    onSuccess: () => {
      // The job exists now, so the active-analyses count is already wrong.
      void queryClient.invalidateQueries({ queryKey: jobsKey })
      void queryClient.invalidateQueries({ queryKey: dealsKey })
    },
  })
}
